import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  IconButton,
  Typography,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Box,
  Alert,
  CircularProgress,
  InputAdornment,
  FormControlLabel,
  Switch,
  Autocomplete
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ownAuthSession from '../../../services/auth/ownAuthSession';
import firebaseDataService from '../../../services/FirebaseDataService';
import { getCurrencySettings } from '../../../services/migration/companyDataService';
import { ACCOUNTING_API } from '../../../config/api';
import { accountingApi } from '../../../config/apiClient';
import { useAuth } from '../../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';

interface AddBankAccountProps {
  open: boolean;
  onClose: () => void;
  accountToEdit?: any; // Düzenlenecek hesap (opsiyonel)
  lockedBranchId?: string | null; // ✅ Manager modu: şube kilidi
}

// Şube arayüzü
interface Branch {
  id: string;
  name: string;
}

const AddBankAccount: React.FC<AddBankAccountProps> = ({ open, onClose, accountToEdit, lockedBranchId = null }) => {
  const { t } = useTranslation('accounting');
  const IBAN_RAW_MAX_LENGTH = 26;
  const IBAN_DISPLAY_MAX_LENGTH = 32;
  // ✅ AuthContext'ten şubeleri al
  const { branches } = useAuth();
  
  // Düzenleme modu kontrolü
  const isEditMode = !!accountToEdit;
  
  // Form state
  const [bankName, setBankName] = useState('');
  const [branchName, setBranchName] = useState(''); // Bankanın kendi şubesi (opsiyonel text)
  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null); // GTS şube sistemi
  const [authorizedPerson, setAuthorizedPerson] = useState(''); // Banka yetkilisi
  const [authorizedPhone, setAuthorizedPhone] = useState(''); // Yetkili telefon
  const [accountNumber, setAccountNumber] = useState('');
  const [iban, setIban] = useState('');
  const [currency, setCurrency] = useState('TRY');
  const [isPosAccount, setIsPosAccount] = useState(false);
  const [isCheckAccount, setIsCheckAccount] = useState(false);
  
  // Yükleme durumları
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);
  
  // Para birimi seçenekleri için state
  const [availableCurrencies, setAvailableCurrencies] = useState<{code: string, active: boolean}[]>([
    { code: 'TRY', active: true } // Varsayılan olarak TRY her zaman olacak
  ]);

  // Modal açıldığında şirket ID'sini ve verileri yükleme
  useEffect(() => {
    if (open) {
      loadCompanyAndData();
      
      // Düzenleme modu ise formu doldur
      if (accountToEdit) {
        populateFormForEdit(accountToEdit);
      }
    }
    
  }, [open, accountToEdit]);

  // ✅ Manager modu: şube otomatik seçilir ve kilitlenir
  useEffect(() => {
    if (open && lockedBranchId && branches.length > 0) {
      const branch = branches.find((b: Branch) => b.id === lockedBranchId);
      if (branch) {
        setSelectedBranch(branch);
      }
    }
  }, [open, lockedBranchId, branches]);

  const loadCompanyAndData = async () => {
    try {
      setInitialLoading(true);
      
      // Kullanıcı oturum açmış mı kontrol et
      const uid = ownAuthSession.getCurrentUid();
      if (!uid) {
        setInitialLoading(false);
        setError(t('bank.addAccount.errors.sessionNotFound'));
        return;
      }
      
      // Kullanıcının şirket ID'sini al (Cloud Functions)
      const userData = await firebaseDataService.getUserData(uid);
      
      if (!userData) {
        setInitialLoading(false);
        setError(t('bank.addAccount.errors.userNotFound'));
        return;
      }
      
      const companyId = userData.companyId;
      setCompanyId(companyId || null);
      
      if (!companyId) {
        setInitialLoading(false);
        setError(t('bank.addAccount.errors.companyNotFound'));
        return;
      }
      
      // Para birimlerini yükle
      loadCurrencies(companyId);
      
    } catch (error) {
      console.error('Şirket veya veri yüklenirken hata:', error);
      setInitialLoading(false);
      setError(t('bank.addAccount.errors.loadFailed'));
    }
  };

  // Düzenleme için formu doldur
  const populateFormForEdit = (account: any) => {
    console.log('📝 Düzenleme modu: Form dolduruluyor...', account);
    setBankName(account.bankName || '');
    setBranchName(account.branchName || '');
    setAccountNumber(account.accountNumber || '');
    setIban(formatIbanForDisplay(getRawIban(account.iban || '')));
    setCurrency(account.currency || 'TRY');
    setIsPosAccount(account.isPosAccount || false);
    setIsCheckAccount(account.isCheckAccount || false);
    setAuthorizedPerson(account.authorizedPerson || '');
    setAuthorizedPhone(account.authorizedPhone || '');
    
    // Şube seçimi
    if (account.branchId && branches.length > 0) {
      const branch = branches.find((b: Branch) => b.id === account.branchId);
      setSelectedBranch(branch || null);
    }
  };

  // Para birimlerini yükle (TEK SEFERLİK - Cloud Functions)
  const loadCurrencies = async (companyId: string) => {
    try {
      const currencyData = await getCurrencySettings(companyId);
      
      // Varsayılan para birimi TRY'yi hazırla
      const currencies: {code: string, active: boolean}[] = [
        { code: 'TRY', active: true } // TRY her zaman aktif olacak
      ];
      
      if (currencyData) {
        // Veritabanından gelen para birimleri
        Object.entries(currencyData).forEach(([code, active]: [string, any]) => {
          // TRY zaten eklendiği için tekrar eklemeye gerek yok
          if (code !== 'TRY' && active === true) {
            currencies.push({ code, active: true });
          }
        });
      }
      
      console.log('Aktif para birimleri yüklendi:', currencies);
      setAvailableCurrencies(currencies);
      
      // Eğer seçili para birimi artık mevcut değilse, TRY'ye ayarla
      const currencyExists = currencies.some(c => c.code === currency && c.active);
      
      if (!currencyExists) {
        setCurrency('TRY');
      }
    } catch (error) {
      console.error('Para birimi yükleme hatası:', error);
      // Offline fallback
      setAvailableCurrencies([{ code: 'TRY', active: true }]);
    } finally {
      setInitialLoading(false);
    }
  };

  // Form alanlarını kontrol et
  const isFormValid = () => {
    return bankName.trim() !== '' && accountNumber.trim() !== '';
  };

  // Para birimi sembolünü döndür
  const getCurrencySymbol = (code: string): string => {
    switch(code) {
      case 'TRY': return '₺';
      case 'USD': return '$';
      case 'EUR': return '€';
      case 'GBP': return '£';
      case 'JPY': return '¥';
      case 'CHF': return 'Fr.';
      case 'AUD': return 'A$';
      case 'CAD': return 'C$';
      case 'CNY': return '¥';
      default: return code;
    }
  };

  // IBAN'ı normalize/format et
  const getRawIban = (value: string): string => value.toUpperCase().replace(/\s/g, '');

  const normalizeIbanInput = (value: string): string => {
    const alphanumeric = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (!alphanumeric) return '';

    const numericPart = alphanumeric.replace(/^TR/i, '').replace(/\D/g, '').slice(0, IBAN_RAW_MAX_LENGTH - 2);
    const typedTrPrefix = alphanumeric.startsWith('T');

    // Türkiye IBAN girişini TR ile sabitle
    return numericPart || typedTrPrefix ? `TR${numericPart}` : '';
  };

  const formatIbanForDisplay = (rawIban: string): string => rawIban.replace(/(.{4})/g, '$1 ').trim();

  const handleIbanChange = (value: string) => {
    const normalized = normalizeIbanInput(value);
    setIban(formatIbanForDisplay(normalized));
  };

  // IBAN formatını kontrol et (Temel kontrol)
  const isValidIBAN = (iban: string): boolean => {
    // Türkiye IBAN formatı kontrol (TR + 2 kontrol rakamı + 5 banka kodu + 1 rezerve + 16 hesap no)
    const regex = /^TR\d{2}\d{5}\d{1}\d{16}$/;
    return regex.test(getRawIban(iban));
  };

  // Hesap numarasını kontrol et
  const isValidAccountNumber = (accountNumber: string): boolean => {
    // Basit bir kontrol - en az 5 rakam
    return /^\d{5,}$/.test(accountNumber.replace(/\s/g, ''));
  };

  // Kaydetme işlemi (SQL API)
  const handleSave = async () => {
    if (!isFormValid()) {
      setError(t('bank.addAccount.errors.requiredFields'));
      return;
    }

    const rawIban = getRawIban(iban);

    if (rawIban !== '' && !isValidIBAN(rawIban)) {
      setError(t('bank.addAccount.errors.invalidIban'));
      return;
    }
    
    if (!isValidAccountNumber(accountNumber)) {
      setError(t('bank.addAccount.errors.invalidAccountNumber'));
      return;
    }

    if (!companyId) {
      setError(t('bank.addAccount.errors.companyNotFound'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (isEditMode) {
        // Düzenleme modu
        console.log('✏️ Banka hesabı güncelleniyor (SQL)...');
        
        const updatedBankAccount = {
          id: accountToEdit.id,
          companyId: companyId,
          bankName: bankName.trim(),
          branchName: branchName.trim() || null,
          branchId: selectedBranch?.id || null,
          authorizedPerson: authorizedPerson.trim() || null,
          authorizedPhone: authorizedPhone.trim() || null,
          accountNumber: accountNumber.trim(),
          iban: rawIban || null,
          currency,
          isPosAccount,
          isCheckAccount,
          updatedBy: ownAuthSession.getCurrentEmail() || 'System'
        };

        console.log('📤 Güncellenen veri:', updatedBankAccount);
        console.log('🔍 selectedBranch:', selectedBranch);
        console.log('🔍 branchId değeri:', updatedBankAccount.branchId);
        console.log('🔍 branchId tipi:', typeof updatedBankAccount.branchId);
        
        // SQL API'ye güncelleme isteği (POST kullanıyoruz, PUT değil)
        const response = await accountingApi.post(ACCOUNTING_API.BANK_ACCOUNT_UPDATE(Number(accountToEdit.id)), updatedBankAccount);
        const result = response.data;
        console.log('✅ Banka hesabı güncellendi (SQL):', result);
      } else {
        // Yeni ekleme modu
        console.log('🏦 Yeni banka hesabı oluşturuluyor (SQL)...');
        
        const newBankAccount = {
          companyId: companyId,
          bankName: bankName.trim(),
          branchName: branchName.trim() || null,
          branchId: selectedBranch?.id || null,
          authorizedPerson: authorizedPerson.trim() || null,
          authorizedPhone: authorizedPhone.trim() || null,
          accountNumber: accountNumber.trim(),
          iban: rawIban || null,
          currency,
          isPosAccount,
          isCheckAccount,
          createdBy: ownAuthSession.getCurrentEmail() || 'System'
        };

        console.log('📤 Gönderilen veri:', newBankAccount);
        console.log('🔍 selectedBranch:', selectedBranch);
        console.log('🔍 branchId değeri:', newBankAccount.branchId);
        console.log('🔍 branchId tipi:', typeof newBankAccount.branchId);
        
        // SQL API'ye kaydet
        const response = await accountingApi.post(ACCOUNTING_API.BANK_ACCOUNTS, newBankAccount);
        const result = response.data;
        console.log('✅ Banka hesabı oluşturuldu (SQL):', result);
      }
      
      // Form alanlarını temizle ve modalı kapat
      resetForm();
      onClose();
    } catch (error: any) {
      console.error('❌ Banka hesabı kaydedilirken hata oluştu:', error);
      setError(`${t('bank.addAccount.errors.saveFailed')}: ${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Form alanlarını sıfırla
  const resetForm = () => {
    setBankName('');
    setBranchName('');
    setSelectedBranch(null);
    setAuthorizedPerson('');
    setAuthorizedPhone('');
    setAccountNumber('');
    setIban('');
    setCurrency('TRY');
    setIsPosAccount(false);
    setIsCheckAccount(false);
    setError(null);
  };

  // Dialog kapanma işleyicisi
  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      sx={{ '& .MuiDialog-paper': { borderRadius: 2 } }}
    >
      <DialogTitle
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <AccountBalanceIcon sx={{ mr: 1 }} />
          <Typography variant="h6">
            {isEditMode ? t('bank.addAccount.titleEdit') : t('bank.addAccount.titleAdd')}
          </Typography>
        </Box>
        <IconButton edge="end" color="inherit" onClick={handleClose} aria-label={t('bank.common.close')}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 2, mt: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {initialLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={2}>
            {/* Banka Adı */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label={t('bank.addAccount.fields.bankName')}
                value={bankName}
                onChange={(e) => setBankName(e.target.value)}
                error={bankName.trim() === ''}
                helperText={bankName.trim() === '' ? t('bank.addAccount.validation.bankNameRequired') : ''}
                size="small"
              />
            </Grid>
            
            {/* Banka Şube Adı */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('bank.addAccount.fields.bankBranchName')}
                value={branchName}
                onChange={(e) => setBranchName(e.target.value)}
                size="small"
                helperText={t('bank.addAccount.fields.bankBranchNameHint')}
              />
            </Grid>
            
            {/* Hesap Numarası */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label={t('bank.addAccount.fields.accountNumber')}
                value={accountNumber}
                onChange={(e) => setAccountNumber(e.target.value)}
                error={accountNumber.trim() === '' || !isValidAccountNumber(accountNumber)}
                helperText={
                  accountNumber.trim() === '' ? t('bank.addAccount.validation.accountNumberRequired') :
                  !isValidAccountNumber(accountNumber) ? t('bank.addAccount.validation.accountNumberInvalid') : ''
                }
                size="small"
              />
            </Grid>
            
            {/* IBAN */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="IBAN"
                value={iban}
                onChange={(e) => handleIbanChange(e.target.value)}
                error={iban.trim() !== '' && !isValidIBAN(iban)}
                helperText={iban.trim() !== '' && !isValidIBAN(iban) ? t('bank.addAccount.validation.ibanInvalid') : ''}
                size="small"
                placeholder="TR00 0000 0000 0000 0000 0000 00"
                inputProps={{ maxLength: IBAN_DISPLAY_MAX_LENGTH }}
              />
            </Grid>
            
            {/* Para Birimi */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('bank.addAccount.fields.currency')}</InputLabel>
                <Select
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  label={t('bank.addAccount.fields.currency')}
                >
                  {availableCurrencies
                    .filter(currency => currency.active)
                    .map(currency => (
                      <MenuItem key={currency.code} value={currency.code}>
                        {`${currency.code} (${getCurrencySymbol(currency.code)})`}
                      </MenuItem>
                    ))
                  }
                </Select>
              </FormControl>
            </Grid>
            
            {/* Şirket Şubesi */}
            <Grid item xs={12} sm={6}>
              <Autocomplete
                id="branch-select"
                options={branches}
                value={selectedBranch}
                onChange={(_, newValue) => setSelectedBranch(newValue)}
                getOptionLabel={(option) => option.name}
                disabled={!!lockedBranchId}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label={lockedBranchId ? t('current.filters.lockedBranch', { defaultValue: 'Şube (kilitli)' }) : t('bank.addAccount.fields.companyBranchOptional')}
                    size="small"
                    helperText={lockedBranchId ? undefined : t('bank.addAccount.fields.companyBranchHint')}
                  />
                )}
                fullWidth
              />
            </Grid>
            
            {/* Banka Yetkilisi */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('bank.addAccount.fields.authorizedPerson')}
                value={authorizedPerson}
                onChange={(e) => setAuthorizedPerson(e.target.value)}
                size="small"
                helperText={t('bank.addAccount.fields.authorizedPersonHint')}
              />
            </Grid>
            
            {/* Yetkili Telefon */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('bank.addAccount.fields.authorizedPhone')}
                value={authorizedPhone}
                onChange={(e) => setAuthorizedPhone(e.target.value)}
                size="small"
                helperText={t('bank.addAccount.fields.authorizedPhoneHint')}
                placeholder="0532 123 45 67"
              />
            </Grid>
            
            {/* POS ve Çek Hesabı - EN ALT */}
            <Grid item xs={12}>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: 1,
                pt: 2,
                borderTop: '1px solid',
                borderColor: 'divider'
              }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {t('bank.addAccount.fields.accountType')}
                </Typography>
                <FormControlLabel
                  control={
                    <Switch 
                      checked={isPosAccount}
                      onChange={(e) => setIsPosAccount(e.target.checked)}
                    />
                  }
                  label={t('bank.addAccount.fields.posAccount')}
                />
                <FormControlLabel
                  control={
                    <Switch 
                      checked={isCheckAccount}
                      onChange={(e) => setIsCheckAccount(e.target.checked)}
                    />
                  }
                  label={t('bank.addAccount.fields.checkAccount')}
                />
              </Box>
            </Grid>
          </Grid>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, pb: 2 }}>
        <Button onClick={handleClose} variant="outlined" size="small">{t('bank.common.cancel')}</Button>
        <Button 
          onClick={handleSave} 
          variant="contained"
          size="small"
          disabled={loading || !isFormValid()}
        >
          {loading ? (
            <>
              <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
              {t('bank.common.saving')}
            </>
          ) : (
            isEditMode ? t('bank.common.update') : t('bank.common.save')
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddBankAccount; 